export type FieldBindingKind = 'logical' | 'raw';

export interface FieldBinding {
  kind: FieldBindingKind;
  key: string;
}

export interface LogicalField {
  id: 'title' | 'articleNumber' | 'measure';
  label: string;
  aliases: string[];
}

export const LOGICAL_FIELDS: LogicalField[] = [
  {
    id: 'title',
    label: 'Titel',
    aliases: ['produktnamn', 'Produktnamn', 'Produktnamn_vin_id'],
  },
  {
    id: 'articleNumber',
    label: 'Artikelnummer',
    aliases: ['artikelnummer', 'Artikelnummer', 'articleNumber', 'artikelnummer_vin_id'],
  },
  {
    id: 'measure',
    label: 'Mått',
    aliases: ['vikt_antal_validoo', 'volym_vin_id'],
  },
];

export const LOGICAL_FIELD_IDS = LOGICAL_FIELDS.map((field) => field.id);

export interface CatalogAsset {
  title?: string;
  articleNumber?: string;
  measure?: string;
  metadata?: Record<string, unknown> | null;
  product?: { name?: string; articleNumber?: string } | null;
}

export function catalogValueItems(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => catalogValueItems(item)).filter(Boolean);
  }
  const text = String(value).trim();
  if (!text) return [];
  const lis = text.match(/<li\b[^>]*>[\s\S]*?<\/li>/gi);
  if (lis?.length) {
    return lis
      .map((item) => item.replace(/<\/?li\b[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);
  }
  return text
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split(/\r?\n/)
    .map((item) => item.replace(/^[-*•]\s+/, '').trim())
    .filter(Boolean);
}

export function normalizeCatalogValue(value: unknown): string {
  return catalogValueItems(value).join('\n');
}

export function isListBindingKey(key?: string | null): boolean {
  return /^uspar$/i.test(String(key || '').trim());
}

export function readMetadataValue(
  metadata: CatalogAsset['metadata'],
  keys: string[]
): string {
  if (!metadata) return '';
  const record = metadata as { get?: (key: string) => unknown };
  for (const key of keys) {
    const raw = typeof record.get === 'function' ? record.get(key) : metadata[key];
    const value = normalizeCatalogValue(raw);
    if (value) return value;
  }
  return '';
}

export function resolveLogicalFields(fields?: LogicalField[]): LogicalField[] {
  return fields?.length ? fields : LOGICAL_FIELDS;
}

export function logicalFieldById(id: string, fields?: LogicalField[]): LogicalField | undefined {
  return resolveLogicalFields(fields).find((field) => field.id === id);
}

export function fieldBindingKey(binding: FieldBinding | undefined | null): string | null {
  if (!binding?.key) return null;
  return binding.key;
}

export function resolveLogicalValue(asset: CatalogAsset, id: string, fields?: LogicalField[]): string {
  const field = logicalFieldById(id, fields);
  if (id === 'title') {
    return (
      normalizeCatalogValue(asset.product?.name)
      || readMetadataValue(asset.metadata, field?.aliases || [])
      || normalizeCatalogValue(asset.title)
    );
  }
  if (id === 'articleNumber') {
    return (
      normalizeCatalogValue(asset.product?.articleNumber)
      || normalizeCatalogValue(asset.articleNumber)
      || readMetadataValue(asset.metadata, field?.aliases || [])
    );
  }
  if (id === 'measure') {
    return (
      normalizeCatalogValue(asset.measure)
      || readMetadataValue(asset.metadata, field?.aliases || [])
    );
  }
  return readMetadataValue(asset.metadata, field?.aliases || [id]);
}

export function resolveBindingValue(asset: CatalogAsset, binding: FieldBinding, fields?: LogicalField[]): string {
  if (binding.kind === 'raw') {
    return readMetadataValue(asset.metadata, [binding.key]);
  }
  return resolveLogicalValue(asset, binding.key, fields);
}

export function fieldValuesFromAsset(asset: CatalogAsset, fields?: LogicalField[]): Record<string, string> {
  const catalog = resolveLogicalFields(fields);
  const values: Record<string, string> = {};
  for (const field of catalog) {
    values[field.id] = resolveLogicalValue(asset, field.id, catalog);
  }
  const metadata = asset.metadata;
  if (metadata && typeof metadata === 'object') {
    const record = metadata as { entries?: () => Iterable<[unknown, unknown]> };
    const entries = typeof record.entries === 'function'
      ? Array.from(record.entries())
      : Object.entries(metadata);
    for (const [key, raw] of entries) {
      const name = String(key);
      if (!name || (values[name] != null && values[name] !== '')) continue;
      const value = normalizeCatalogValue(raw);
      if (value && values[name] == null) values[name] = value;
    }
  }
  return values;
}

export type TemplateFieldSource = {
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  order?: number;
};

export type CatalogTemplateSource = {
  name?: string;
  fullPageLayoutProfile?: string;
  fields?: TemplateFieldSource[];
};

export type TemplateFieldOption = {
  key: string;
  label: string;
};

export function templateFieldBindingKey(field: TemplateFieldSource): string | null {
  if (String(field.type || '') === 'separator') return null;
  if (/ignore this/i.test(String(field.label || ''))) return null;
  const id = String(field.id || '').trim();
  const name = String(field.name || '').trim();
  if (id && !/^field_/i.test(id)) return id;
  return name || id || null;
}

export function bindableTemplateFields(fields: TemplateFieldSource[] | undefined): TemplateFieldOption[] {
  const options: TemplateFieldOption[] = [];
  const seen = new Set<string>();
  const ordered = [...(fields || [])].sort((left, right) => (left.order || 0) - (right.order || 0));
  for (const field of ordered) {
    const key = templateFieldBindingKey(field);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({
      key,
      label: String(field.label || key).trim() || key,
    });
  }
  return options;
}

export function pickCatalogTemplate<T extends CatalogTemplateSource>(
  templates: T[],
  catalog: 'product' | 'wine',
): T | undefined {
  const byProfile = templates.find((template) => template.fullPageLayoutProfile === catalog);
  if (byProfile) return byProfile;
  if (catalog === 'product') {
    return templates.find((template) => /produkt/i.test(template.name || ''));
  }
  return templates.find((template) => /^vin$/i.test(String(template.name || '').trim()));
}

export function catalogFieldOptions(templates: CatalogTemplateSource[]): {
  product: TemplateFieldOption[];
  wine: TemplateFieldOption[];
} {
  return {
    product: bindableTemplateFields(pickCatalogTemplate(templates, 'product')?.fields),
    wine: bindableTemplateFields(pickCatalogTemplate(templates, 'wine')?.fields),
  };
}

export function fieldLabel(key: string, options: TemplateFieldOption[] = [], fields?: LogicalField[]): string {
  return logicalFieldById(key, fields)?.label
    || options.find((option) => option.key === key)?.label
    || key;
}

export function normalizeFieldBinding(value: unknown): FieldBinding | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as { kind?: unknown; key?: unknown };
  const key = String(record.key || '').trim();
  if (!key) return undefined;
  const kind = record.kind === 'raw' ? 'raw' : 'logical';
  if (kind === 'logical' && !logicalFieldById(key, undefined)) {
    return { kind: 'raw', key };
  }
  return { kind, key };
}
